'use client';

import { c as _c } from "react/compiler-runtime";
import { useWallets } from '@morfi/backend-sdk-v2/client';
import BN from 'bn.js';
import { useFormatter, useTranslations } from 'next-intl';
import { OutcomeTokenPicker } from '@/components/gift/outcome-token-picker';
import { AnimatedAmountInput } from '@/components/trade/animated-amount-input';
import { Label } from '@/components/ui/label';
import { SegmentedControl, type SegmentedControlItem } from '@/components/ui/segmented-control';
import { SelectField, type SelectFieldItem } from '@/components/ui/select-field';
import { walletDisplayLabel } from '@/components/wallet/wallet-display-label';
import { d6ToDisplayUsdc, MIN_GIFT_AMOUNT_USDC_D6 } from '@/config/gift';
import { D6_SCALE } from '@/config/solana';
import { useFormattedCurrency } from '@/lib/currency';
import { estimateOutcomeTokenSpotValueFromStringsD6, parseOutcomeTokenDisplayAmountD6 } from '@/lib/gifts/outcome-token-spot-value';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useWalletBalances } from '@/queries/wallet-balance';
import { selectStepAttempted, useGiftFormStore } from '@/stores/gift-form';
interface SendGiftStep2Props {
  /** Sender's USDC wallet balance — display number (lossy; submit uses BN). */
  balanceUsdc: number;
  /** Sender's USDC wallet balance as a raw u64 D6 bigint. Used for Max precision. */
  balanceD6Bigint: bigint;
  /** Sender's wallet pubkey for the outcome-token holdings query. */
  senderAddress: string;
  /** Quick-amount tier values (USD anchors filtered to balance). Built by orchestrator. */
  usdcQuickAmounts: readonly {
    label: string;
    value: number;
  }[];
  tokenQuickAmounts: readonly {
    label: string;
    value: number;
  }[];
}

/** Same precise stringifier used in the orchestrator's Max button. */
function d6BigintToExactDisplay(d6: bigint): string {
  const ZERO = BigInt(0);
  const D6_BIGINT = BigInt(D6_SCALE);
  if (d6 < ZERO) return `-${d6BigintToExactDisplay(-d6)}`;
  const whole = d6 / D6_BIGINT;
  const frac = d6 % D6_BIGINT;
  if (frac === ZERO) return whole.toString();
  const fracStr = frac.toString().padStart(6, '0').replace(/0+$/, '');
  return `${whole}.${fracStr}`;
}

/**
 * Step 2 of the gift flow — pick the source wallet, asset, and amount.
 *
 * Uses `<AnimatedAmountInput>` (the same component as the trade modal). The
 * USDC and outcome-token branches share the same UI; only the underlying
 * amount semantics differ (display USDC vs USD value of tokens).
 *
 * Message + anonymity moved to step 3 (the optional "Add a message" step).
 */
export function SendGiftStep2(props) {
  const $ = _c(62);
  const t = useTranslations("gift.send");
  const tSteps = useTranslations("gift.send.steps");
  const format = useFormatter();
  const {
    formatBalance,
    formatD6,
    currencySymbol,
    toUsdc,
    exchangeRate
  } = useFormattedCurrency();
  const {
    wallets
  } = useWallets();
  const {
    enabled: multiWalletEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  const scopedWallets = multiWalletEnabled ? wallets : primaryWallet === null ? [] : [primaryWallet];
  const activeWallets = scopedWallets.filter(_temp);
  const walletAddresses = activeWallets.map(_temp2);
  const {
    perWallet: walletBalances
  } = useWalletBalances(walletAddresses);
  let t0;
  if ($[0] !== formatBalance || $[1] !== walletBalances) {
    t0 = w_0 => {
      const bal = walletBalances.get(w_0.address) ?? 0;
      return {
        value: w_0.id,
        label: <span className="flex w-full items-center justify-between gap-2"><span className="truncate">{walletDisplayLabel(w_0)}</span><span className="shrink-0 text-caption text-foreground-secondary">{formatBalance(bal)}</span></span>
      };
    };
    $[0] = formatBalance;
    $[1] = walletBalances;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const walletItems = activeWallets.map(t0);
  const senderWalletId = useGiftFormStore(_temp3);
  const setSenderWalletId = useGiftFormStore(_temp4);
  const asset = useGiftFormStore(_temp5);
  const amount = useGiftFormStore(_temp6);
  const selectedHolding = useGiftFormStore(_temp7);
  const maxOverrideD6Str = useGiftFormStore(_temp8);
  const setAsset = useGiftFormStore(_temp9);
  const setAmount = useGiftFormStore(_temp10);
  const setSelectedHolding = useGiftFormStore(_temp11);
  const setMaxOverrideD6Str = useGiftFormStore(_temp12);
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectStepAttempted(2);
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const attempted = useGiftFormStore(t1);
  const fixedSourceWallet = multiWalletEnabled ? undefined : activeWallets[0];
  const balanceD6BN = new BN(props.balanceD6Bigint.toString());
  const amountNum = Number.parseFloat(amount);
  const usdcAmountD6 = maxOverrideD6Str !== null ? new BN(maxOverrideD6Str) : Number.isFinite(amountNum) && amountNum > 0 ? new BN(Math.floor(toUsdc(amountNum) * D6_SCALE)) : null;
  const usdcExceedsBalance = usdcAmountD6?.gt(balanceD6BN) === true;
  const usdcAmountTooLow = usdcAmountD6?.lt(MIN_GIFT_AMOUNT_USDC_D6) === true;
  const minAmountUsdc = d6ToDisplayUsdc(MIN_GIFT_AMOUNT_USDC_D6);
  const tokenBalanceDisplay = selectedHolding !== null ? Number(selectedHolding.balanceD6) / D6_SCALE : 0;
  const tokenHoldingSpotValueD6 = selectedHolding === null ? null : estimateOutcomeTokenSpotValueFromStringsD6(selectedHolding.balanceD6, selectedHolding.currentPriceD9);
  const tokenHoldingValueD6 = new BN(tokenHoldingSpotValueD6?.toString() ?? "0");
  const tokenInputBaseD6 = maxOverrideD6Str !== null ? new BN(maxOverrideD6Str) : new BN(parseOutcomeTokenDisplayAmountD6(amount)?.toString() ?? "0");
  const tokenBalanceD6 = selectedHolding !== null ? new BN(selectedHolding.balanceD6) : new BN(0);
  const tokenExceedsBalance = selectedHolding !== null && tokenInputBaseD6.gt(tokenBalanceD6);
  let t2;
  if ($[4] !== maxOverrideD6Str || $[5] !== setAmount || $[6] !== setMaxOverrideD6Str) {
    t2 = function handleAmountChange(next) {
      setAmount(next);
      if (maxOverrideD6Str !== null) {
        setMaxOverrideD6Str(null);
      }
    };
    $[4] = maxOverrideD6Str;
    $[5] = setAmount;
    $[6] = setMaxOverrideD6Str;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleAmountChange = t2;
  let t3;
  if ($[8] !== exchangeRate || $[9] !== props.balanceD6Bigint || $[10] !== setAmount || $[11] !== setMaxOverrideD6Str) {
    t3 = function handleUsdcMax() {
      if (props.balanceD6Bigint > BigInt(0)) {
        const usdcDisplay = Number(d6BigintToExactDisplay(props.balanceD6Bigint));
        const localDisplay = usdcDisplay * exchangeRate;
        setAmount(localDisplay.toString());
        setMaxOverrideD6Str(props.balanceD6Bigint.toString());
      }
    };
    $[8] = exchangeRate;
    $[9] = props.balanceD6Bigint;
    $[10] = setAmount;
    $[11] = setMaxOverrideD6Str;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleUsdcMax = t3;
  let t4;
  if ($[13] !== maxOverrideD6Str || $[14] !== setAmount || $[15] !== setMaxOverrideD6Str) {
    t4 = function handleUsdcQuick(v) {
      setAmount(String(v));
      if (maxOverrideD6Str !== null) {
        setMaxOverrideD6Str(null);
      }
    };
    $[13] = maxOverrideD6Str;
    $[14] = setAmount;
    $[15] = setMaxOverrideD6Str;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handleUsdcQuick = t4;
  let t5;
  if ($[17] !== selectedHolding || $[18] !== setAmount || $[19] !== setMaxOverrideD6Str) {
    t5 = function handleTokenMax() {
      if (selectedHolding !== null && BigInt(selectedHolding.balanceD6) > BigInt(0)) {
        setAmount(d6BigintToExactDisplay(BigInt(selectedHolding.balanceD6)));
        setMaxOverrideD6Str(selectedHolding.balanceD6);
      }
    };
    $[17] = selectedHolding;
    $[18] = setAmount;
    $[19] = setMaxOverrideD6Str;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const handleTokenMax = t5;
  let t6;
  if ($[21] !== maxOverrideD6Str || $[22] !== setAmount || $[23] !== setMaxOverrideD6Str) {
    t6 = function handleTokenQuick(v_0) {
      setAmount(String(v_0));
      if (maxOverrideD6Str !== null) {
        setMaxOverrideD6Str(null);
      }
    };
    $[21] = maxOverrideD6Str;
    $[22] = setAmount;
    $[23] = setMaxOverrideD6Str;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const handleTokenQuick = t6;
  const showAmountRequiredError = attempted && (!Number.isFinite(amountNum) || amountNum <= 0);
  const showHoldingRequiredError = attempted && asset === "outcome_token" && selectedHolding === null;
  const t7 = "flex flex-col gap-4";
  const t8 = multiWalletEnabled ? <SelectField label={t("fromWallet.label")} value={senderWalletId} onValueChange={setSenderWalletId} items={walletItems} fullWidth={true} /> : fixedSourceWallet !== undefined ? <div className="flex flex-col gap-1.5"><Label>{t("fromWallet.label")}</Label><div className="flex items-center justify-between gap-2 rounded-control border bg-background-primary px-3 py-2.5 text-body font-medium"><span className="truncate">{walletDisplayLabel(fixedSourceWallet)}</span><span className="shrink-0 text-caption text-foreground-secondary">{formatBalance(walletBalances.get(fixedSourceWallet.address) ?? 0)}</span></div></div> : null;
  let t9;
  if ($[25] !== t) {
    t9 = t("asset.label");
    $[25] = t;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t9) {
    t10 = <Label htmlFor="gift-asset">{t9}</Label>;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t) {
    t11 = t("asset.usdc");
    $[29] = t;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11) {
    t12 = {
      key: "usdc",
      label: t11
    };
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t) {
    t13 = t("asset.outcomeToken");
    $[33] = t;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13) {
    t14 = {
      key: "outcome_token",
      label: t13
    };
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t12 || $[38] !== t14) {
    t15 = [t12, t14];
    $[37] = t12;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const t16 = t15 satisfies SegmentedControlItem[];
  let t17;
  if ($[40] !== setAsset) {
    t17 = key => {
      if (key === "usdc" || key === "outcome_token") {
        setAsset(key);
      }
    };
    $[40] = setAsset;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== asset || $[43] !== t16 || $[44] !== t17) {
    t18 = <SegmentedControl variant="tab-surface" asTabs={true} items={t16} active={asset} onChange={t17} layoutId="gift-asset-tabs-pill" fullWidth={true} className="rounded-pill bg-background-tertiary p-1" />;
    $[42] = asset;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t10 || $[47] !== t18) {
    t19 = <div className="flex flex-col gap-2">{t10}{t18}</div>;
    $[46] = t10;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== asset || $[50] !== props.senderAddress || $[51] !== selectedHolding || $[52] !== setSelectedHolding || $[53] !== showHoldingRequiredError || $[54] !== t || $[55] !== tSteps) {
    t20 = asset === "outcome_token" ? <div className="flex flex-col gap-2"><Label htmlFor="gift-outcome-pick">{t("asset.outcomeToken")}</Label>{props.senderAddress.length > 0 ? <OutcomeTokenPicker walletAddress={props.senderAddress} selectedKey={selectedHolding !== null ? `${selectedHolding.marketPubkey}__${selectedHolding.outcomeIndex}` : null} onChange={setSelectedHolding} /> : null}{showHoldingRequiredError ? <p className="text-caption text-feedback-error-content" role="alert">{tSteps("errors.holdingRequired")}</p> : null}</div> : null;
    $[49] = asset;
    $[50] = props.senderAddress;
    $[51] = selectedHolding;
    $[52] = setSelectedHolding;
    $[53] = showHoldingRequiredError;
    $[54] = t;
    $[55] = tSteps;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  const t21 = asset === "usdc" || selectedHolding !== null ? <div className="flex flex-col gap-2"><Label>{asset === "usdc" ? t("amount.label") : t("amount.tokenLabel")}</Label><AnimatedAmountInput value={amount} onChange={handleAmountChange} currencySymbol={asset === "usdc" ? currencySymbol : ""} compact={true} quickAmounts={asset === "usdc" ? props.usdcQuickAmounts : props.tokenQuickAmounts} onQuickAmount={asset === "usdc" ? handleUsdcQuick : handleTokenQuick} onMaxClick={asset === "usdc" ? handleUsdcMax : handleTokenMax} disabled={asset === "usdc" ? props.balanceD6Bigint <= BigInt(0) : tokenHoldingValueD6.lte(new BN(0))} inputLabel={asset === "usdc" ? t("amount.label") : t("amount.tokenLabel")} maxLabel={t("amount.max")} /><p className="text-caption text-foreground-secondary">{asset === "usdc" ? t("amount.balance", {
        amount: formatBalance(props.balanceUsdc, "full")
      }) : t("asset.availableTokensAndValue", {
        tokens: format.number(tokenBalanceDisplay, {
          maximumFractionDigits: 2
        }),
        value: formatD6(tokenHoldingValueD6.toString(), "full")
      })}</p>{asset === "usdc" && usdcAmountTooLow ? <p className="text-caption text-feedback-error-content" role="alert">{t("amount.tooLow", {
        min: formatBalance(minAmountUsdc, "full")
      })}</p> : null}{asset === "usdc" && usdcExceedsBalance ? <p className="text-caption text-feedback-error-content" role="alert">{t("amount.exceedsBalance")}</p> : null}{asset === "outcome_token" && tokenExceedsBalance ? <p className="text-caption text-feedback-error-content" role="alert">{t("amount.exceedsBalance")}</p> : null}{showAmountRequiredError ? <p className="text-caption text-feedback-error-content" role="alert">{tSteps("errors.amountRequired")}</p> : null}</div> : null;
  let t22;
  if ($[57] !== t19 || $[58] !== t20 || $[59] !== t21 || $[60] !== t8) {
    t22 = <div className={t7}>{t8}{t19}{t20}{t21}</div>;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
    $[60] = t8;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  return t22;
}
function _temp12(s_8) {
  return s_8.setMaxOverrideD6Str;
}
function _temp11(s_7) {
  return s_7.setSelectedHolding;
}
function _temp10(s_6) {
  return s_6.setAmount;
}
function _temp9(s_5) {
  return s_5.setAsset;
}
function _temp8(s_4) {
  return s_4.maxOverrideD6Str;
}
function _temp7(s_3) {
  return s_3.selectedHolding;
}
function _temp6(s_2) {
  return s_2.amount;
}
function _temp5(s_1) {
  return s_1.asset;
}
function _temp4(s_0) {
  return s_0.setSenderWalletId;
}
function _temp3(s) {
  return s.senderWalletId;
}
function _temp2(w) {
  return w.address;
}
function _temp(wallet) {
  return wallet.archivedAt === null && wallet.isAvailable !== false;
}